import React from "react";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/content/services";

const SECTIONS = [
  {
    title: "1. Who we are",
    body: [
      `Lanvaus Private Limited ("Lanvaus", "we", "us") operates lanvaus.com and provides cybersecurity services. For the purposes of the Digital Personal Data Protection Act, 2023, Lanvaus is the Data Fiduciary for personal data collected through this website.`,
      `Registered address: ${CONTACT.addressLines.slice(1).join(" ")} Contact: ${CONTACT.email}.`,
    ],
  },
  {
    title: "2. What we collect",
    body: [
      "Consultation form: your name, work email, company, phone number, service of interest, industry and the message you write.",
      "Resource downloads: your name, work email, company and optional phone number, together with which document you requested.",
      "Server logs: standard technical data such as IP address, browser type and pages requested, retained for security and abuse prevention.",
      "We do not use advertising cookies, third-party trackers or session recording on this website.",
    ],
  },
  {
    title: "3. Why we use it",
    body: [
      "To respond to your enquiry and deliver the document you requested.",
      "To follow up once about relevant security guidance where you have asked us to, and to maintain a record of the enquiry.",
      "To protect the website against abuse and to meet our own legal and audit obligations.",
      "We do not sell your data, we do not share it with advertisers, and we do not add you to bulk marketing lists.",
    ],
  },
  {
    title: "4. Legal basis and consent",
    body: [
      "We process the personal data you submit on the basis of the consent you give when you complete a form, and for the legitimate purpose of responding to your request. You may withdraw consent at any time by writing to us.",
    ],
  },
  {
    title: "5. Who can access it",
    body: [
      "Access is limited to Lanvaus personnel who need it to respond to you. Data is held in our own application database and object storage with access controls and audit logging.",
      "Where a processor is used for infrastructure or email delivery, it is bound by contract to process data only on our instructions.",
    ],
  },
  {
    title: "6. How long we keep it",
    body: [
      "Enquiry and download records are retained for up to 24 months from the last interaction, unless a longer period is required for a contract or by law. Server logs are retained for up to 180 days.",
      "Engagement evidence created during client work is governed by the engagement contract, not by this policy, and is deleted on the schedule agreed there.",
    ],
  },
  {
    title: "7. Your rights",
    body: [
      "You may request access to the personal data we hold about you, request correction or completion of it, request erasure where it is no longer needed, withdraw consent, or nominate another person to exercise your rights in the event of death or incapacity.",
      `To exercise any of these rights, or to raise a grievance, write to ${CONTACT.email}. We will acknowledge within 7 days and respond substantively within 30 days.`,
    ],
  },
  {
    title: "8. Security",
    body: [
      "The website is served over TLS. Form submissions are stored in an access-controlled database. Downloadable documents are served through short-lived, single-use links rather than public URLs. Administrative access to the site requires authentication with hashed credentials and is rate-limited.",
    ],
  },
  {
    title: "9. Children",
    body: [
      "This website is intended for business users. We do not knowingly collect personal data of children. If you believe a child has submitted data, write to us and we will delete it.",
    ],
  },
  {
    title: "10. Changes",
    body: [
      "If this policy changes materially we will update this page and the effective date below. Continued use of the website after a change constitutes acceptance of the updated policy.",
    ],
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <section className="mx-auto max-w-4xl px-5 pb-16 pt-28 sm:px-8 sm:pb-24 sm:pt-40">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#8E9AEA]">Legal</p>
          <h1 className="mt-5 font-display text-[2rem] font-extrabold leading-[1.15] tracking-tight text-white sm:text-4xl lg:text-5xl">
            Privacy <span className="text-[#8E9AEA]">Policy</span>
          </h1>
          <p className="mt-5 text-sm text-[#A6B0C9]">
            Effective 9 June 2026 · Aligned to the Digital Personal Data Protection Act, 2023 (India)
          </p>
        </Reveal>

        <div className="mt-10 sm:mt-14 flex flex-col gap-10">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={Math.min(i, 6) * 0.04}>
              <section data-testid={`privacy-section-${i + 1}`}>
                <h2 className="font-display text-xl font-bold text-white">{s.title}</h2>
                <div className="mt-4 flex flex-col gap-3">
                  {s.body.map((p) => (
                    <p key={p.slice(0, 30)} className="text-base leading-relaxed text-[#DDE3F2]">
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="mt-10 sm:mt-14 rounded-xl border border-white/10 bg-white/[0.03] p-6 text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm">
            This policy describes our actual practices on this website. It is not legal advice, and it does not replace
            the data-processing terms in a signed engagement contract.
          </p>
        </Reveal>
      </section>
    </div>
  );
}
